import { LooseObjectType } from './globalTypes';

export const PROMISE_STATUS = {
  pending: 'pending',
  fulfilled: 'fulfilled',
  rejected: 'rejected',
};

export const API_REQUEST_METHODS = {
  get: { name: 'GET_API', method: 'get' },
  post: { name: 'POST_API', method: 'post' },
  put: { name: 'PUT_API', method: 'put' },
  patch: { name: 'PATCH_API', method: 'patch' },
  delete: { name: 'DELETE_API', method: 'delete' },
};

export const STORAGE_KEYS = {
  persistRoot: 'persist:root',
  inactivity: 'inactivity',
  sessionExpired: 'sessionExpired',
};

/* White Glove Service entitlement key — the `itps:`-prefixed key in the session
   entitlements object (e.g. `entitlements['itps:id_theft_resolution']`). */
export const WGS_ENTITLEMENT = 'itps:id_theft_resolution';

/* White Glove Service feature code — the UNPREFIXED code as it appears in plan
   `features` arrays (offer.planFeatures / availableOffers[].plan.features).
   Mirrors Angular's `hasWhiteGloveFeature` target 'id_theft_resolution'. */
export const WGS_FEATURE_CODE = 'id_theft_resolution';

/* White Glove Service upsell banner gating (mirrors Angular dashboard-carousel).
   Banner shows for DTC + retail-cc-billed offers, or once the account is past the
   setup window, when the user is flagged product-change eligible or the offer is a
   CTP upsell. */
export const WGS_RETAIL_CC_BILLED = 'retail cc billed';
export const WGS_USER_ELIGIBLE_REASON_CODE = 'USER_ELIGIBLE';
export const WGS_CTP_UPSELL_OFFER_TYPE = 'ctp upsell';
export const WGS_SETUP_WINDOW_DAYS = 90;
export const WGS_DTC_GROUP_TYPE = 'dtc';

export const PANGO_BILLING_AUTHORIZATION_TOKEN = `Basic ${import.meta.env.VITE_PANGO_BILLING_AUTH_TOKEN}`;
export const PANGO_REDEEM_AUTHORIZATION_TOKEN = `Basic ${import.meta.env.VITE_PANGO_REDEEM_AUTH_TOKEN}`;
export const IGLOO_SUBKEY = `${import.meta.env.VITE_IGLOO_SUBKEY}`;
export const TU_PUBLIC_KEY = `${import.meta.env.VITE_TU_PUBLIC_KEY}`;
/**
 * Basic-auth header value used when calling the `webcontent/articles/*`
 * endpoints for the insights-and-tips module. Mirrors Angular's
 * `InsightsInterceptor` which swaps the user access token for this key on
 * those requests (identityWeb/.../insights-interceptor.ts). Without it the
 * endpoint returns 403 IAM deny and the article list is empty.
 *
 * Reuses `VITE_ID_VERIFICATION_AUTH_KEY` — the same GitHub secret is used by
 * `idauth-verify/services/idauthVerifyAsync.ts` for the sibling
 * `/admin/attempt/` endpoint that Angular's InsightsInterceptor also covers.
 */
export const WEB_CONTENT_KEY = `Basic ${import.meta.env.VITE_ID_VERIFICATION_AUTH_KEY}`;

export const API_ENDPOINTS = {
  baseURL: import.meta.env.VITE_BASE_URL,
  pangoBaseURL: import.meta.env.VITE_PANGO_BASE_URL,
  pwmgrURL: import.meta.env.VITE_PWMGR_URL,
  usAddressUrl: import.meta.env.VITE_US_ADDRESS_URL,
  breachCodes: 'breachcodes',
  /* Enrollment — one key per flow (thin / 5-step / flex), plus the shared offer-info lookup */
  thinEnrollment: 'api/client/enrollments/thin',
  fiveStepEnrollment: 'api/client/enrollments',
  flexEnrollment: 'api/client/enrollments/flex/user',
  flexComplimentaryEnrollment: 'api/client/enrollments/flex',
  flexEnrollmentPayment: 'api/client/enrollments/flex/payment',
  /* AOL / partner single-page enrollment's pre-flight token check. */
  yahooTokenValidation: 'api/client/yahoo/validation',
  /* Offer/plan info by offerCode + planCode — used by the 5-step and flex flows.
     Distinct from `enrollmentInfo` below, which looks the same up by token. */
  enrollmentInformation: 'api/client/enrollments/info',
  /* Pre-flight duplicate/format check on the 5-step account step's email field */
  enrollmentEmailValidation: 'api/client/enrollments/validation',
  /* Marketing attribution reported against a new enrollment, authenticated by the
     enrollment token rather than a bearer token */
  marketingAnalytics: 'api/client/marketing/analytics',
  /* Spirit Airlines Free Spirit promo link-up after a billed enrollment. Sits on the API
     host root, not behind `api/` — identityWeb builds it from its API_URL directly. */
  spiritAirlinesPromotions: 'spirit_airlines/promotions',
  userSession: 'api/client/sessions',
  thinSession: 'api/client/sessions/thin',
  refreshToken: 'api/client/sessions/refresh',
  darkWebWatchlist: 'api/client/monitored_items',
  statesOfUS: 'api/ref/countries/us/states_provinces',
  alerts: '/api/client/alerts',
  userProfile: 'api/client/users',
  /* Durable tour flags.
     Deliberately NOT in WITH_BEARER_TOKEN: `includeAuthorizationHeader`
     (`services/api/apiAsync.ts:77-87`) runs the bearer loop first and the
     no-bearer loop second, both by SUBSTRING. `userProfile`
     (`api/client/users`) is in WITH_OUT_BEARER_TOKEN and is a prefix of this
     path, so the second loop would overwrite any `Bearer` with the raw token
     regardless. This endpoint therefore inherits the same auth shape as every
     other `api/client/users/*` call, which is the family convention. */
  userAttribute: 'api/client/users/attribute',
  contactDetails: 'api/client/users',
  passwordScan: 'password-exposure',
  getMarketingOffer: '/api/client/marketing/offer',
  getMarketingOfferDetails: '/api/ref/marketing/offers',
  forgotPassword: '/api/client/unverified_users/forgot_password',
  forgotUsername: '/api/client/unverified_users/forgot_username',
  validateToken: '/api/client/unverified_users/validate?token=',
  verifyToken: '/api/client/unverified_users/verify?token=',
  resetPassword: '/api/client/unverified_users/{token}/reset_password',
  dbooUser: 'dboo/user',
  fetchDbooUser: 'dboo/v2/user',
  notifications: 'api/client/notifications',
  getBrandDetails: 'brands',
  enrollmentInfo: '/api/client/enrollments/info?token=',
  planDetails: '/api/client/plans/',
  accountProfile: 'api/client/users/account',
  updateDevice: '/api/client/recipients/channels/sms/devices/undefined',
  createDevice: '/api/client/recipients/channels/sms/devices',
  preferences: '/api/client/recipients/preferences',
  companyCode: '/api/ref/companies',
  termsOfService: '/cms/',
  /* Brand-scoped legal documents (service terms, privacy, website terms, refund).
     Angular calls this `/ref/legal_policies/` and its ApiInterceptor prepends `/api`,
     so the real path is the `api/ref/...` one below — not the bare `/ref/...` string.
     Brand scoping is server-side off the `X-Idg-Brand` header that `makeAPIRequest`
     already stamps on every call. */
  legalPolicies: 'api/ref/legal_policies/',
  /* Secure document upload */
  secureUploadSettings: '/userfile/client/setting',
  secureUploadUrl: '/userfile/client/upload-url',
  secureUserFileClient: '/userfile/client',
  /* Security - 2FA */
  sessionSendSecurityOTP: '/api/client/sessions/2fa/send',
  sessionVerifySecurityOTP: '/api/client/sessions/2fa/verify',
  getSecurityChannels: '/api/client/users/2fa',
  addSecurityChannel: '/api/client/users/2fa/add',
  removeSecurityChannel: '/api/client/users/2fa/remove',
  addChannelVerifySecurityOTP: '/api/client/users/2fa/verify',
  accountSendSecurityOTP: '/api/client/users/account/2fa/send',
  accountVerifySecurityOTP: '/api/client/users/account/2fa/verify',
  /* Convert To Pay */
  zuoraRSATokens: '/pango-billing/zuora/rsa-signatures',
  getAvailableOffers: '/api/client/marketing/offers/available',
  getSubscriptionInfo: '/api/client/subscribers/',
  eligibility: 'api/client/users/eligibility',
  conversionEligibility: 'api/client/users/conversion_eligibility',
  getSubscriptions: '/api/client/subscriptions',
  createZuoraAccount: '/pango-billing/client/account/create',
  acceptLegalPolicies: '/api/client/accepted_legal_policies',
  updateZuoraAccount: '/pango-billing/client/account/update',
  changeMarketingOffer: '/api/client/marketing/offers/change',
  getPangoBillingOfferDetails: '/pango-billing/client/offer/get',
  findPlan: '/pango-billing/client/plan/find',
  redeemSession: 'auth/session/redeem',
  getBillingAccountInfo: 'pango-billing/client/account/info',
  updateBillingAccountInfo: 'pango-billing/client/account/update',
  /* Collect marketing email opt-in */
  emailOptInEventTracking: 'event-tracking/user/web',
  userEventTracking: 'event-tracking/user',
  /* Home Title Monitoring */
  htmonProperties: 'hometitle-monitoring/address',
  /* Public Records Monitoring */
  publicRecordProperties: 'public-records/DEED',
  // Synthetic Identity Report (minor). Path only — `?childId=` is appended at
  // call time, and the token-set lookup in `includeAuthorizationHeader` is a
  // substring match, so the query string does not break header injection.
  ssnt: 'public-records/SSNT',
  creditScore: 'credit_scores',
  creditScoreTU: '/api/client/credit_scores',
  creditFile: 'credit-file',
  creditSimulations: 'credit-simulations',
  idVerificationTuid: 'id_verification/tuid',
  alertsGroup: '/api/client/alerts/group',
  dashboardSummary: '/api/client/dashboard/summary',
  /* ID Auth */
  dit: 'id_verification/dit',
  smsInitiate: 'id_verification/smfa/sms/initiate',
  smsValidate: 'id_verification/smfa/sms/validate',
  emailInitiate: 'id_verification/smfa/email/initiate',
  emailValidate: 'id_verification/smfa/email/validate',
  quizQuestions: 'id_verification/IDAUTH/questions',
  quizSubmit: 'id_verification/IDAUTH/submitAnswers',
  smfaRedirect: 'id_verification/admin/attempt',
  /* Credit Report — 3B (uses main VITE_BASE_URL) */
  creditReport3b: 'api/client/credit_reports',
  creditReport3bSummary: 'api/client/credit_reports/summary',
  /* Credit Report — TBCR (uses main VITE_BASE_URL) */
  creditReportTbcr: 'credit-reports/tbcr',
  /* Credit Report — CRTB (uses main VITE_BASE_URL) */
  creditReportCrtb: 'credit-reports/CRTB',
  /* Credit Report — 1B (uses main VITE_BASE_URL; bureau code appended at call site) */
  creditReportOneBureau: 'credit-reports',
  /* Transaction Monitoring */
  ftmItems: 'ftm/items',
  ftmTransactions: 'ftm/transactions',
  ftmTransactionsAutocomplete: 'ftm/transactions/autocomplete/name',
  ftmLinkToken: 'ftm/items/link_token',
  ftmItemRelink: 'ftm/items',
  ftmThresholds: 'ftm/v2/thresholds_config',
  /* Social Media Monitoring */
  socialMonitoringAccounts: 'social-monitoring/accounts',
  socialMonitoringToken: 'social-monitoring',
  invitors: 'api/client/invitors',
  invitorsAdults: 'api/client/invitors/adults',
  // Base path for `${invitorsAdultById}/${id}` DELETE requests.
  invitorsAdultById: 'api/client/invitors/adults',
  invitorsChildren: 'api/client/invitors/children',
  // Base path for `${invitorsChildById}/${id}` DELETE requests.
  invitorsChildById: 'api/client/invitors/children',
  invitorsNewAdult: 'api/client/invitors/newAdult',
  // Child dark-web monitored-items feed. Matches Angular
  // `watchlist.service.ts:getChildMonitoredItems` — the non-`/tools/`
  // variant. Angular also has a `/tools/monitored_items/dependents/children`
  // endpoint (retry-wrapped) that returns the same shape; we use the
  // plain endpoint since it's the one surfaced on child-scoped views.
  invitorChildItems: 'api/client/monitored_items/dependents/children',
  /* Security Freeze */
  securityFreeze: 'security-freeze',
  /* HealthLock */
  healthLockDetails: 'healthlock/user/details',
  healthLockSso: 'healthlock/user/sso',
  /* Insights & Tips (external article feed only) */
  // NOT in the bearer-token sets — Angular's InsightsInterceptor swaps the
  // user access token for the WEB_CONTENT_KEY Basic-auth key on these two
  // URLs. The React thunk sets the Authorization header explicitly per
  // call, so the WITH_*_BEARER_TOKEN matcher must NOT fire (any match
  // would overwrite the header back to the user token and get IAM-denied).
  insightsAndTipsSummary: 'webcontent/articles/summary',
  insightsAndTipsArticleById: 'webcontent/articles',
};

export const getCreditReportTbcrDetailEndpoint = (id: string) =>
  `credit-reports/${id}/detail`;

// DELETE /social-monitoring/accounts/{accountId} (childId appended as a query param at the call site).
export const getSocialMonitoringAccountByIdEndpoint = (accountId: string) =>
  `social-monitoring/accounts/${accountId}`;

export const WITH_BEARER_TOKEN = new Set([
  API_ENDPOINTS.refreshToken,
  API_ENDPOINTS.userSession,
  API_ENDPOINTS.secureUploadSettings,
]);

/**
 * The canonical legal document paths. `/terms-of-service` and `/privacy-policy` are the
 * only legal entry points; the other three documents are child routes of them. Shared so
 * the route table, the legal-page nav and the footer cannot drift apart, and so the
 * identityWeb redirects have one place to point at.
 */
export const LEGAL_PATHS = {
  termsOfService: '/terms-of-service',
  websiteTermsOfUse: '/terms-of-service/website-terms-of-use',
  privacyPolicy: '/privacy-policy',
  refundPolicy: '/privacy-policy/refund-policy',
  idTheft: '/privacy-policy/id-theft',
  insurance: '/insurance',
  faq: '/faq',
};

export const WITH_OUT_BEARER_TOKEN = new Set([
  API_ENDPOINTS.thinSession,
  API_ENDPOINTS.alerts,
  API_ENDPOINTS.breachCodes,
  API_ENDPOINTS.fiveStepEnrollment,
  /* Enrollment is unauthenticated, so this resolves to a null token exactly as every
     `api/client/enrollments` call above already does (those match by prefix). */
  API_ENDPOINTS.yahooTokenValidation,
  API_ENDPOINTS.darkWebWatchlist,
  API_ENDPOINTS.passwordScan,
  API_ENDPOINTS.dbooUser,
  API_ENDPOINTS.fetchDbooUser,
  API_ENDPOINTS.userProfile,
  API_ENDPOINTS.contactDetails,
  API_ENDPOINTS.notifications,
  API_ENDPOINTS.planDetails,
  API_ENDPOINTS.accountProfile,
  API_ENDPOINTS.preferences,
  API_ENDPOINTS.updateDevice,
  API_ENDPOINTS.createDevice,
  API_ENDPOINTS.getMarketingOffer,
  API_ENDPOINTS.termsOfService,
  API_ENDPOINTS.secureUploadUrl,
  API_ENDPOINTS.secureUserFileClient,
  API_ENDPOINTS.getSubscriptions,
  API_ENDPOINTS.getSecurityChannels,
  API_ENDPOINTS.addSecurityChannel,
  API_ENDPOINTS.removeSecurityChannel,
  API_ENDPOINTS.addChannelVerifySecurityOTP,
  API_ENDPOINTS.accountSendSecurityOTP,
  API_ENDPOINTS.accountVerifySecurityOTP,
  API_ENDPOINTS.getAvailableOffers,
  API_ENDPOINTS.acceptLegalPolicies,
  API_ENDPOINTS.changeMarketingOffer,
  API_ENDPOINTS.getSubscriptionInfo,
  API_ENDPOINTS.htmonProperties,
  API_ENDPOINTS.publicRecordProperties,
  API_ENDPOINTS.ssnt,
  API_ENDPOINTS.creditScore,
  API_ENDPOINTS.creditScoreTU,
  API_ENDPOINTS.creditFile,
  API_ENDPOINTS.creditSimulations,
  API_ENDPOINTS.userEventTracking,
  API_ENDPOINTS.createZuoraAccount,
  API_ENDPOINTS.updateZuoraAccount,
  API_ENDPOINTS.getPangoBillingOfferDetails,
  API_ENDPOINTS.findPlan,
  API_ENDPOINTS.getBillingAccountInfo,
  API_ENDPOINTS.updateBillingAccountInfo,
  API_ENDPOINTS.idVerificationTuid,
  API_ENDPOINTS.alertsGroup,
  API_ENDPOINTS.dashboardSummary,
  API_ENDPOINTS.dit,
  API_ENDPOINTS.smsInitiate,
  API_ENDPOINTS.smsValidate,
  API_ENDPOINTS.emailInitiate,
  API_ENDPOINTS.emailValidate,
  API_ENDPOINTS.quizQuestions,
  API_ENDPOINTS.quizSubmit,
  API_ENDPOINTS.creditReport3b,
  API_ENDPOINTS.creditReport3bSummary,
  API_ENDPOINTS.creditReportTbcr,
  API_ENDPOINTS.creditReportCrtb,
  API_ENDPOINTS.creditReportOneBureau,
  /* Transaction Monitoring */
  API_ENDPOINTS.ftmItems,
  API_ENDPOINTS.ftmTransactions,
  API_ENDPOINTS.ftmTransactionsAutocomplete,
  API_ENDPOINTS.ftmLinkToken,
  API_ENDPOINTS.ftmThresholds,
  API_ENDPOINTS.socialMonitoringAccounts,
  API_ENDPOINTS.socialMonitoringToken,
  API_ENDPOINTS.invitors,
  API_ENDPOINTS.invitorsAdults,
  API_ENDPOINTS.invitorsAdultById,
  API_ENDPOINTS.invitorsChildren,
  API_ENDPOINTS.invitorsChildById,
  API_ENDPOINTS.invitorsNewAdult,
  API_ENDPOINTS.invitorChildItems,
  API_ENDPOINTS.securityFreeze,
  API_ENDPOINTS.healthLockDetails,
  API_ENDPOINTS.healthLockSso,
]);

export const GENERAL = {
  mobileSize: 700,
  tabletSize: 940,
  alertsPerPage: 10,
  phoneNumberMask: '(000) 000-0000',
  active: 'active',
  defaultErrorMsg:
    'Something went wrong. Please try again or contact the Customer Care Center.',
  tryAgainLaterErrorMsg:
    // eslint-disable-next-line quotes
    "We're currently unable to process your request. Please wait and try again later.",
};

export const OS = {
  mac: 'mac',
  windows: 'windows',
  ios: 'ios',
  android: 'android',
  other: 'other',
};

export const BROWSER = {
  chrome: 'Chrome',
  msEdge: 'Edge',
  firefox: 'Firefox',
  safari: 'Safari',
  other: 'Other',
};

export const OS_BROWSER_SUPPORT_MESSAGES = {
  browserNotSupport:
    'Password Defense is only available for Chrome, Safari, Edge, and Firefox.',
  osNotSupport:
    'Password Defense is only available for devices running an Apple or Android operating system.',
  vpnInTouchNotSupport:
    'VPN In Touch is only available for iOS, Android, Windows and Mac.',
  antivirusNotSupport:
    'UltraAV is only available for Windows, Mac and Android.',
};

export const VPN_IN_TOUCH_DOWNLOAD_LINKS = {
  [OS.windows]:
    'https://firebasestorage.googleapis.com/v0/b/vpn-in-touch-windows/o/Installer%2FVPNInTouch.exe?alt=media&token=d7c84dcd-c219-4690-bfd1-f6695c2c9687',
  [OS.android]:
    'https://play.google.com/store/apps/details?id=com.vpnintouch.android&hl=en&gl=US',
  [OS.ios]: 'https://apps.apple.com/us/app/vpn-in-touch/id464241430',
  [OS.mac]: 'https://apps.apple.com/us/app/vpn-in-touch/id464241430',
};

export const PASSWORD_DEFENSE_INSTALL_LINKS = {
  [OS.android]:
    'https://play.google.com/store/apps/details?id=co.pango.passworddefense',
  [OS.ios]: 'https://apps.apple.com/app/password-defense/id6503000237',
};

export const PASSWORD_DEFENSE_EXTENSION_LINKS = {
  [BROWSER.chrome]:
    'https://chromewebstore.google.com/detail/password-defense/jldcelchacngobkjhgboanjfdmolihha',
  [BROWSER.msEdge]:
    'https://microsoftedge.microsoft.com/addons/detail/password-defense/bjamgiegaombbhmmomjfgcfblelcapol',
  [BROWSER.firefox]:
    'https://addons.mozilla.org/en-US/firefox/addon/password-defense/',
  [BROWSER.safari]:
    'https://apps.apple.com/us/app/password-defense-extension/id6505006082?mt=12',
};

export const ULTRA_ANTIVIRUS_DOWNLOAD_LINKS = {
  [OS.windows]:
    'https://packages.ultrasecureav.com/UltraAvDesktop/windows/app/UltraAVSetup.exe?_sm_nck=1',
  [OS.mac]:
    'https://elite-s3-bucket.s3.us-west-1.amazonaws.com/ultraav/UltraAV.dmg',
  [OS.android]:
    'https://play.google.com/store/apps/details?id=com.anchorfree.ultraav',
};

export const DEEP_LINK_APPS = {
  vpnInTouch: 'vpnInTouch',
  passwordDefense: 'passwordDefense',
};

export const DEEP_LINKS = {
  [DEEP_LINK_APPS.vpnInTouch]: 'https://www.pango.co/vpn-in-touch/',
  [DEEP_LINK_APPS.passwordDefense]: 'https://www.pango.co/password-defense/',
};

export const WINDOWS_DEEP_LINKS = {
  [DEEP_LINK_APPS.vpnInTouch]: 'vpnintouch://',
  [DEEP_LINK_APPS.passwordDefense]: 'passworddefense://',
};

export const LINKS = {
  cyex: 'http://www.cyex.com/',
  cyexScan: 'https://scan.cyex.com/',
  duckDuckGo: 'https://duckduckgo.com',
  duckDuckGoTermsOfUse: 'https://duckduckgo.com/terms',
  duckDuckGoPrivacyPolicy: 'https://duckduckgo.com/privacy',
  passwordScanTermsOfService: 'https://haveibeenpwned.com/TermsOfUse',
  passwordScanPrivacyPolicy: 'https://haveibeenpwned.com/Privacy',
};

export const REG_EX = {
  lettersSpacesHyphensApostrophes: /^[a-zA-Z\u00C0-\u02AF\s'-]{1,}$/,
  lettersSpacesPeriodsHyphensApostrophes:
    /^(?!\s)(?!.*\s$)[a-zA-Z0-9\s\-.'#]{1,}$/,
  lettersNumbersSpacesHyphensSlashes: /^[a-zA-Z0-9\s\-/]*$/,
  lettersNumbersSpacesPeriodsHyphensSlashesHashes: /^[a-zA-Z0-9\s\-./#]{1,}$/,
  lettersNumbersSpacesHyphensPeriods: /^(?!\s)(?!.*\s$)[a-zA-Z0-9\s\-.]{1,}$/,
  lettersNumbersSpacesHyphensUnderscore:
    /^(?!\s)(?!.*\s$)[a-zA-Z0-9\s\-._]{1,}$/,
  lettersSpacesAsterisksHyphens: /^(?!\s)(?!.*\s$)[a-zA-Z0-9*\s-]{1,}$/,
  lettersSpacesUnderscoresHyphensPeriods: /^[a-zA-Z0-9\s_.-]{1,}$/,
  lettersNumbersUnderscoreHyphenPeriods: /^[a-zA-Z0-9_.-]{1,}$/,
  lettersNumbersUnderscoreHyphen: /^[a-zA-Z0-9_-]{1,}$/,
  lettersNumbersPeriodUnderscore: /^[a-zA-Z0-9_.]+$/,
  lettersNumbersPeriod: /^[a-zA-Z0-9.]+$/,
  lettersNumbersUnderscore: /^[a-zA-Z0-9_]+$/,
  lettersNumbers: /^[a-z0-9]*$/i,
  lettersNumbersSpaces: /^[a-z0-9\s]*$/i,
  lettersSpacesHyphen: /^[a-z\s-]*$/i,
  lettersSpaces: /^[a-z\s]*$/i,
  startsWithLetterNumberHyphenUnderscore: /^[a-zA-Z][a-zA-Z0-9\-_]*$/,
  numbers: /^[\d]{0,}$/,
  phoneNumber: /^[2-9][0-9]*$/,
  noSpace: /^\S*$/,
  websiteDomain:
    /^((https?|ftp):\/\/)?(([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}|localhost)(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(#[-a-z\d_]*)?$/i,
  networkIP:
    /\b(?:\d{1,3}\.){3}\d{1,3}\b|(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}\b/,
  email:
    /^\s*[-a-z0-9~!$%^&*_=+}{\\'?]+(\.[-a-z0-9~!$%^&*_=+}{\\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.([a-z]{2,})|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?\s*$/i,
  domainFromUrl: /^(?:https?:\/\/)?(?:[^@/\n]+@)?(?:www\.)?([^:/?\n]+)/gim,
  startsWithoutPlusMinusEqualtoAttherate: /^(?![+=\-@])[\s\S]*$/,
  lettersNumbersSpacesHyphensSlashesHashesPeriods: /^[a-zA-Z0-9\s\-./#]*$/,
  ssn: /^(?!666|000|9\d{2})(?!\d{3}00)(?!\d{5}0000)\d{9}$|^(9\d{2})([-]?)(5\d|6[0-5]|7\d|8[0-8]|9[0-2]|9[4-9])([-]?)(\d{4})$/,
  usernamePattern:
    /^[a-zA-Z0-9]+[#$&+\-=@_.]*(?:[a-zA-Z0-9]+[#$&+\-=@_.]+)*[a-zA-Z0-9]+$/,
};

export const DARK_WEB_WATCH_LIST: LooseObjectType = {
  initialShowCount: 3,
  lockItems: ['name', 'birthDate', 'ssn'],
  acceptedCardTypes: {
    visa: 'visa',
    mastercard: 'mastercard',
    'american-express': 'american express',
    discover: 'discover',
  },
};

export const DATA_BROKER_OPT_OUT = {
  inProgress: 'IN_PROGRESS',
  blocked: 'MORE_INFO_NEEDED',
  scheduledDateFormat: 'MMM DD',
  optOutDateFormat: 'MMM DD, YYYY',
};

export const DIGITAL_VAULT = {
  previewFileTypes: ['jpeg', 'jpg', 'png'],
  addedDateFormat: 'MMM DD, YYYY',
  fallbackDocumentType: 'livingWill',
};

export const SUFFIX_OPTIONS = [
  {
    value: '',
    label: 'None',
  },
  {
    value: 'JR',
    label: 'JR',
  },
  {
    value: 'SR',
    label: 'SR',
  },
  {
    value: 'I',
    label: 'I',
  },
  {
    value: 'II',
    label: 'II',
  },
  {
    value: 'III',
    label: 'III',
  },
  {
    value: 'IV',
    label: 'IV',
  },
  {
    value: 'V',
    label: 'V',
  },
  {
    value: 'VI',
    label: 'VI',
  },
  {
    value: 'VII',
    label: 'VII',
  },
  {
    value: 'VIII',
    label: 'VIII',
  },
];

export const PHONE_NUMBER_TYPE_OPTIONS = [
  {
    value: 'mobile',
    label: 'Mobile',
  },
  {
    value: 'work',
    label: 'Work',
  },
  {
    value: 'home',
    label: 'Home',
  },
  {
    value: 'main',
    label: 'Main',
  },
  {
    value: 'fax',
    label: 'Fax',
  },
  {
    value: 'pager',
    label: 'Pager',
  },
  {
    value: 'other',
    label: 'Other',
  },
];

export const VISA_NUMBER_TYPE_OPTIONS = [
  {
    value: 'green_card_number',
    label: 'Green Card number',
  },
  {
    value: 'visa_number',
    label: 'Visa number',
  },
];

export const ACCOUNT_TYPE_OPTIONS = [
  {
    value: 'checking',
    label: 'Checking',
  },
  {
    value: 'savings',
    label: 'Savings',
  },
  {
    value: 'retirement',
    label: 'Retirement',
  },
  {
    value: 'investment',
    label: 'Investment',
  },
];

export const CRYPTO_WALLET_TYPE_OPTIONS = [
  {
    value: 'financial_crypto_ada_wallet',
    label: 'Cardano',
  },
  {
    value: 'financial_crypto_bch_wallet',
    label: 'Bitcoin',
  },
  {
    value: 'financial_crypto_btc_wallet',
    label: 'SoFi',
  },
  {
    value: 'financial_crypto_bth_wallet',
    label: 'BTH',
  },
  {
    value: 'financial_crypto_dash_wallet',
    label: 'Dash',
  },
  {
    value: 'financial_crypto_doge_wallet',
    label: 'Dogecoin',
  },
  {
    value: 'financial_crypto_eth_wallet',
    label: 'Ethereum',
  },
  {
    value: 'financial_crypto_ltc_wallet',
    label: 'Litecoin',
  },
  {
    value: 'financial_crypto_neo_wallet',
    label: 'Neon',
  },
  {
    value: 'financial_crypto_usdt_wallet',
    label: 'Tether',
  },
  {
    value: 'financial_crypto_xem_wallet',
    label: 'Zen',
  },
  {
    value: 'financial_crypto_xmr_wallet',
    label: 'Monero',
  },
  {
    value: 'financial_crypto_xrp_wallet',
    label: 'XRP',
  },
];

export const PASSWORD_STRENGTH_INDICATOR: LooseObjectType = {
  Bad: {
    label: 'Bad',
    tooltip: 'This password is insecure and puts your account at risk',
  },
  Weak: {
    label: 'Weak',
    tooltip:
      'This password lacks complexity and uniqueness. It may be easily guessed or be susceptible to dictionary attacks.',
  },
  Good: {
    label: 'Good',
    tooltip:
      'This password offers moderate protection for your account. It could be better.',
  },
  Strong: {
    label: 'Strong',
    tooltip:
      'This password offers better than average protection. It is strong enough to make it difficult to crack.',
  },
  Excellent: {
    label: 'Excellent',
    tooltip: 'This password provides the highest level of security',
  },
};

export const ENROLLMENT_ERROR_MESSAGE: LooseObjectType = {
  offerBrandMismatch:
    'We encountered an issue. The offer you’re enrolling with is not valid. Contact support if you need assistance.',
};

export const ENV: 'test' | 'stage' | 'prod' =
  import.meta.env.VITE_PS_ENV || 'test';
export const smartyEmbeddedKey = import.meta.env.VITE_SMARTY_API_KEY;
export const smartyAddressUrl = import.meta.env.VITE_US_ADDRESS_URL;

/* Security 2FA */
export const SECURITY_CHANNEL_TYPES = {
  email: 'email',
  sms: 'sms',
  call: 'call',
} as const;
export const SECURITY_ACTION_TYPES = {
  session: 'session',
  account: 'account',
  add: 'add',
} as const;

/* Privacy global plan code */
export const PRIVACY_GLOBAL_PLAN_CODE = 'psglobind';

export const CTP_ELGIBILITY_DETAILS = {
  subscriber: 'subscriber',
  pendingCancelation: 'pending cancellation',
  pendingCtp: 'BREACH_COMPLIMENTARY_LIMITED_OFFER_CHANGE_SCHEDULED',
  ctp: 'ctp',
  orderSummary: 'orderSummary',
};

/**
 * Account-holder type on the user record (`user.type`).
 *
 * Cancellation is primary-holder-only: Angular branches the entire Billing
 * details body on `type === 'primary'` (`billing-term.component.html:3`), so a
 * non-primary holder has no cancellation affordance at all (BRD FR-035 / BR-014).
 * `secondary` is the only non-primary value the flow produces in practice.
 */
export const USER_TYPES = {
  primary: 'primary',
  secondary: 'secondary',
} as const;

/* EyeOn support pages */
export const EYEON_SUPPORT_PAGES = [
  {
    title: 'Miss Hosting SE/NO',
    href: 'https://misshosting.help',
  },
  {
    title: 'Domene',
    href: 'https://www.domene.no/kontakt',
  },
  {
    title: 'Hemsida24',
    href: 'https://hemsida24.help/sv',
  },
];

/* Plan entitlements */
export const PLAN_ENTITLEMENT_KEYS = {
  customerNotificationMethods: 'itps:plan_customer_notification_methods',
  customerCareSupportMethods: 'itps:plan_customer_care_support_methods',
};

/* Finish account setup popup/banner display waiting period in days */
export const finishAccountSetupWaitingPeriod = 90;

export const OFFER_CHANGING = 'OFFER_CHANGING';

/* Credit score features */
export const CREDIT_BUREAUS = {
  experian: 'experian',
  transunion: 'transunion',
  equifax: 'equifax',
} as const;

export const CREDIT_BUREAU_CONFIGS: Record<
  (typeof CREDIT_BUREAUS)[keyof typeof CREDIT_BUREAUS],
  {
    id: (typeof CREDIT_BUREAUS)[keyof typeof CREDIT_BUREAUS];
    displayName: string;
    iconName: string;
    rawAliases: readonly string[];
  }
> = {
  experian: {
    id: 'experian',
    displayName: 'Experian',
    iconName: 'experian-small',
    rawAliases: ['experian', 'xpn'],
  },
  transunion: {
    id: 'transunion',
    displayName: 'TransUnion',
    iconName: 'transunion-small',
    rawAliases: ['transunion', 'tu'],
  },
  equifax: {
    id: 'equifax',
    displayName: 'Equifax',
    iconName: 'equifax-small',
    rawAliases: ['equifax', 'efx'],
  },
} as const;

export const CREDIT_SCORE_STATUS = {
  unknown: 'unknown',
  thinFile: 'thin_file',
  unavailable: 'credit_score_unavailable',
  success: 'success',
  calculated: 'calculated',
} as const;

export const CREDIT_FEATURES = [
  'CREDIT_LOCK',
  'CREDIT_MONITORING_1B',
  'CREDIT_MONITORING_3B',
  'CREDIT_REPORT_1B',
  'CREDIT_REPORT_3B',
  'CREDIT_SCORE',
  'CREDIT_SCORE_3B',
  'CREDIT_SNAPSHOT',
  'CRTB_ORCHESTRATION',
];

/* User Events */
export const USER_EVENTS = {
  viewCtpOptInNotification: 'ICON_CTP_CLICK',
  dismissCtpOptInNotification: 'ICON_CTP_DISMISS',
  viewFinishSetupBanner: 'CC_BANNER_CLICKED',
  viewCtpBanner: 'CTP_BANNER_CLICKED',
  /* Membership cancellation flow (BRD FR-004, FR-011, FR-021, FR-022).
     Analytics event names are application-level and owned by the analytics
     layer, so they belong here rather than in the module's own config —
     same precedent as the CTP and finish-setup events above. */
  cancelRequestInitiated: 'CANCEL_REQUEST_INITIATED',
  cancelReasonSubmitted: 'CANCEL_REASON_SUBMITTED',
  cancelOfferAccepted: 'CANCEL_OFFER_ACCEPTED',
  cancelOffersDeclined: 'CANCEL_OFFERS_DECLINED',
};
/* End of user events */
